import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Detective, CheckCircle, WarningCircle,
  Play, ArrowsClockwise, Clock, Coins, Eye,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; }
}

const STATUS_STYLE = {
  finished: "text-accent border-accent/30",
  running: "text-amber-400 border-amber-500/30",
  error: "text-destructive border-destructive/30",
};

export default function HasDataCollection() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hoursStr, setHoursStr] = useState("");
  const [limitStr, setLimitStr] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, r] = await Promise.all([
        api.get("/admin/collection/config"),
        api.get("/admin/collection/runs"),
      ]);
      setCfg(c.data); setRuns(r.data);
      setHoursStr((c.data.hours || []).join(", "));
      setLimitStr(String(c.data.limit ?? 40));
    } catch (e) { toast.error("Chargement HasData impossible"); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    try {
      const { data } = await api.post("/admin/collection/config", patch);
      setCfg(data);
      setHoursStr((data.hours || []).join(", "));
      setLimitStr(String(data.limit ?? 40));
      toast.success("Configuration mise à jour");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const collectNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/collection/collect", { limit: Number(limitStr) || undefined });
      if (data.error) toast.error(data.error);
      else toast.success(`Collecte lancée · ${data.requests_planned} requête(s) · ~${data.credits_estimate} crédits`);
      setTimeout(load, 4000);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) return <div className="text-muted font-mono text-sm animate-pulse">Chargement HasData…</div>;
  const lastErr = cfg.last_run?.status === "error" ? cfg.last_run?.error : null;

  return (
    <div className="surface rounded-lg p-6 mb-6" data-testid="hasdata-panel">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-accent/12 border border-accent/25 flex items-center justify-center">
            <Detective size={20} weight="duotone" className="text-accent" />
          </div>
          <div>
            <h3 className="font-display font-bold text-fg flex items-center gap-2">
              Indeed France <span className="text-[10px] font-mono text-muted border border-border rounded-full px-2 py-0.5">HasData · fr.indeed.com</span>
            </h3>
            <p className="text-xs text-muted">Collecte automatique des offres IT sur Indeed France. Cabinets de recrutement &amp; intérim exclus.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {cfg.configured ? (
            <span className="flex items-center gap-1 text-xs font-mono text-accent border border-accent/30 rounded-full px-2 py-1" data-testid="hasdata-status"><CheckCircle size={12} weight="fill" /> Configurée</span>
          ) : (
            <span className="flex items-center gap-1 text-xs font-mono text-amber-400 border border-amber-500/30 rounded-full px-2 py-1" data-testid="hasdata-status"><WarningCircle size={12} weight="fill" /> Clé manquante</span>
          )}
          <button onClick={() => saveCfg({ enabled: !cfg.enabled })} data-testid="hasdata-toggle"
            className={`flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-medium ${cfg.enabled ? "bg-accent/12 border-accent/40 text-accent" : "bg-surface border-border text-muted hover:text-fg"}`}>
            <CheckCircle size={13} weight={cfg.enabled ? "fill" : "regular"} /> Auto {cfg.enabled ? "activé" : "désactivé"}
          </button>
        </div>
      </div>

      {lastErr && (
        <div className="mb-4 text-xs bg-destructive/10 border border-destructive/30 text-destructive rounded-md px-3 py-2" data-testid="hasdata-error">
          {lastErr}
        </div>
      )}

      <div className="grid sm:grid-cols-4 gap-3 border-t border-border pt-4 mb-4">
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Clock size={12} /> Heures (UTC)</label>
          <Input className="bg-surface-2 border-border text-xs h-9" value={hoursStr} onChange={(e) => setHoursStr(e.target.value)}
            onBlur={() => saveCfg({ hours: hoursStr })} placeholder="6, 18" data-testid="hasdata-hours" />
        </div>
        <div>
          <label className="overline block mb-1.5 flex items-center gap-1"><Coins size={12} /> Max requêtes / collecte</label>
          <Input type="number" className="bg-surface-2 border-border text-xs h-9" value={limitStr} onChange={(e) => setLimitStr(e.target.value)}
            onBlur={() => saveCfg({ limit: Number(limitStr) })} data-testid="hasdata-limit" />
        </div>
        <div className="sm:col-span-2 flex items-end">
          <button onClick={collectNow} disabled={busy || !cfg.configured} data-testid="hasdata-collect-now"
            className="w-full flex items-center justify-center gap-2 bg-accent text-black font-semibold px-4 py-2 rounded-md hover-lift disabled:opacity-50 h-9">
            <Play size={15} weight="fill" className={busy ? "animate-pulse" : ""} /> {busy ? "Lancement…" : "Collecter maintenant"}
          </button>
        </div>
      </div>
      <p className="text-[11px] text-muted mb-4 flex items-center gap-1.5">
        <Coins size={12} /> Chaque requête = 5 crédits HasData (~15-25 offres). Estimation : {(Number(limitStr) || 0) * 5} crédits par collecte. Mots-clés &amp; localisations gérés dans le panneau partagé ci-dessus.
      </p>

      <div className="border-t border-border pt-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-display font-bold text-fg text-sm">Dernières collectes</h4>
          <button onClick={load} className="text-xs text-muted hover:text-accent flex items-center gap-1"><ArrowsClockwise size={12} /> Rafraîchir</button>
        </div>
        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="hasdata-runs">
          {runs.length === 0 && <span className="text-xs text-muted">Aucune collecte pour l'instant.</span>}
          {runs.map((r) => (
            <div key={r.id} className="text-xs bg-surface-2 border border-border rounded-md px-3 py-2 flex items-center gap-3 flex-wrap">
              <span className={`font-mono border rounded-full px-2 py-0.5 ${STATUS_STYLE[r.status] || "text-muted border-border"}`}>{r.status}</span>
              <span className="text-muted font-mono">{fmtDate(r.created_at)}</span>
              <span className="font-mono text-fg/80">{r.requests_done || 0}/{r.requests_planned || 0} req · {r.records_received || 0} offres</span>
              {r.status === "finished" && (
                <span className="font-mono text-fg/70">{r.created || 0} créées · {r.updated || 0} maj · {r.duplicates || 0} doublons · {r.staffing_dropped || 0} intérim exclus</span>
              )}
              {r.error && <span className="text-destructive">{r.error}</span>}
            </div>
          ))}
        </div>
      </div>

      <p className="text-[11px] text-muted mt-4 flex items-center gap-1.5">
        <Eye size={12} /> La source des signaux (France Travail, Adzuna, Indeed) et les liens d'origine ne sont visibles que par vous (admin), jamais par les ESN.
      </p>
    </div>
  );
}
